import { useState } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc,
  doc,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from '../firebase';

const useDynamicQR = (userId) => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  // Create a dynamic QR code in Firestore
  const createDynamicQR = async (qrData) => {
    if (!userId) {
      throw new Error('User must be authenticated to create dynamic QR codes');
    }

    setIsCreating(true);
    setError('');

    try {
      const {
        qrContent,
        qrType,
        originalPrompt,
        size = 256,
        bgColor = '#ffffff',
        fgColor = '#000000',
        level = 'M',
        title,
        description
      } = qrData;

      // Generate a unique short ID (8 characters)
      const shortId = generateShortId();

      // Create the dynamic QR record in Firestore
      const dynamicQRRef = collection(db, 'dynamic_qr_codes');
      const docRef = await addDoc(dynamicQRRef, {
        userId,
        shortId,
        
        // QR Configuration
        qrContent,
        qrType,
        originalPrompt,
        title: title || originalPrompt.substring(0, 50),
        description: description || '',
        
        // Visual settings
        size,
        bgColor,
        fgColor,
        level,
        
        // Status
        isActive: true,
        isExpired: false,
        expiresAt: null,
        
        // Analytics - UPDATED WITH NEW FIELDS
        scanCount: 0,
        uniqueScans: 0,
        scansToday: 0,
        scansThisWeek: 0,
        lastScannedAt: null,
        scans: [],
        
        // Device Stats - NEW
        deviceStats: {
          mobile: 0,
          desktop: 0,
          tablet: 0
        },
        
        // Geographic Stats - NEW
        geoStats: {},
        topLocation: null,
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastResetDate: serverTimestamp()
      });

      // Return the dynamic QR object with redirect URL
      const redirectUrl = `${window.location.origin}/qr/${shortId}`;
      
      setIsCreating(false);
      
      return {
        id: docRef.id,
        shortId,
        redirectUrl,
        qrContent: redirectUrl, // The QR code will encode the redirect URL
        originalContent: qrContent,
        qrType: 'dynamic',
        originalPrompt,
        title: title || originalPrompt.substring(0, 50),
        description,
        size,
        bgColor,
        fgColor,
        level,
        isActive: true,
        scanCount: 0,
        timestamp: new Date().toISOString()
      };
      
    } catch (err) {
      console.error('Error creating dynamic QR:', err);
      setError(err.message || 'Failed to create dynamic QR code');
      setIsCreating(false);
      throw err;
    }
  };

  // Update dynamic QR content
  const updateDynamicQR = async (qrId, updates) => {
    if (!userId) {
      throw new Error('User must be authenticated');
    }

    try {
      const qrRef = doc(db, 'dynamic_qr_codes', qrId);
      
      await updateDoc(qrRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (err) {
      console.error('Error updating dynamic QR:', err);
      throw err;
    }
  };

  // Toggle QR code active status
  const toggleQRStatus = async (qrId, isActive) => {
    return updateDynamicQR(qrId, { isActive });
  };

  // Set expiration date
  const setExpiration = async (qrId, expiresAt) => {
    return updateDynamicQR(qrId, { 
      expiresAt,
      isExpired: expiresAt ? new Date(expiresAt) < new Date() : false
    });
  };

  // Record a scan event
  const recordScan = async (shortId, scanData = {}) => {
    try {
      // This would be called from your redirect page
      // You'll need to query by shortId first to get the document
      
      const scanEvent = {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        ...scanData
      };

      // Update scan count and add scan event
      // Note: You'll need to implement a Cloud Function or query to find by shortId
      
      return true;
    } catch (err) {
      console.error('Error recording scan:', err);
      return false;
    }
  };

  // Generate a unique short ID
  const generateShortId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  return {
    isCreating,
    error,
    createDynamicQR,
    updateDynamicQR,
    toggleQRStatus,
    setExpiration,
    recordScan
  };
};

export default useDynamicQR;